import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { Avatar, ListItemAvatar, ListItemButton, ListItemText } from "@mui/material";
import { Spinner } from "components/shared/Spinner";
import TitleCover from "components/shared/TitleCover";
import { getInitials } from "helpers/utils";
import type { TPortalType } from "store/types";

import variables from "../../assets/styles/_variables.module.scss";

import type { SearchResults } from "./useCommandPaletteState";

type Props = {
    query: string;
    debouncedQuery: string;
    searchResults: SearchResults;
    isSearching: boolean;
    isPressLike: boolean;
    portalType: TPortalType;
    onCreateTitle: () => void;
    onCreateBeneficiary: () => void;
    onCreatePublication: () => void;
    onCreateArticle: () => void;
    onOpenTitle: (id: number) => void;
    onOpenPublication: (id: number) => void;
    onOpenArticle: (titleId: number, articleId: number) => void;
    onOpenBeneficiary: (id: number) => void;
    onFilterTitleInManagement: (ean13?: string | null, name?: string | null) => void;
    onFilterArticleInManagement: (name?: string | null) => void;
    onFilterBeneficiaryInManagement: (surname?: string | null, name?: string | null) => void;
    t: (key: string) => string;
};

export const DataResultsList = ({
    query,
    debouncedQuery,
    searchResults,
    isSearching,
    isPressLike,
    portalType,
    onCreateTitle,
    onCreateBeneficiary,
    onCreatePublication,
    onCreateArticle,
    onOpenTitle,
    onOpenPublication,
    onOpenArticle,
    onOpenBeneficiary,
    onFilterTitleInManagement,
    onFilterArticleInManagement,
    onFilterBeneficiaryInManagement,
    t,
}: Props) => {
    const showInitialActions = !query.trim() && !debouncedQuery.trim();
    const hasResults =
        !!searchResults &&
        (searchResults.titles.length > 0 ||
            searchResults.beneficiaries.length > 0 ||
            searchResults.articles.length > 0);

    const showSpinner =
        isSearching &&
        debouncedQuery.trim() &&
        (!searchResults ||
            (searchResults.titles.length === 0 &&
                searchResults.beneficiaries.length === 0 &&
                searchResults.articles.length === 0));

    const showEmptyState =
        !isSearching &&
        searchResults &&
        debouncedQuery.trim() &&
        searchResults.titles.length === 0 &&
        searchResults.beneficiaries.length === 0 &&
        searchResults.articles.length === 0;

    const filterLabel = t("CommandPalette.FilterInManagement");

    const renderActionItem = (label: string, onClick: () => void) => (
        <ListItemButton onClick={onClick} className="command-palette__item command-palette__item--action">
            <ListItemAvatar>
                <Avatar
                    sx={{
                        width: 32,
                        height: 32,
                        fontSize: 14,
                        bgcolor: "transparent",
                        color: "#4f46e5",
                    }}
                >
                    <AddOutlinedIcon sx={{ fontSize: 18 }} />
                </Avatar>
            </ListItemAvatar>
            <ListItemText primary={label} className="command-palette__item-text" />
        </ListItemButton>
    );

    return (
        <>
            {showInitialActions && (
                <li>
                    <ul>
                        {isPressLike ? (
                            <>
                                {renderActionItem(
                                    t(`CommandPalette.NewPublication_${portalType}`),
                                    onCreatePublication,
                                )}
                                {renderActionItem(t(`CommandPalette.NewArticle_${portalType}`), onCreateArticle)}
                                {renderActionItem(t("CommandPalette.NewBeneficiar"), onCreateBeneficiary)}
                            </>
                        ) : (
                            <>
                                {renderActionItem(t("CommandPalette.NewTitle"), onCreateTitle)}
                                {renderActionItem(t("CommandPalette.NewBeneficiar"), onCreateBeneficiary)}
                            </>
                        )}
                    </ul>
                </li>
            )}

            {hasResults && searchResults && (
                <>
                    {searchResults.titles.length > 0 && (
                        <li>
                            <ul>
                                {searchResults.titles.map((title) => (
                                    <ListItemButton
                                        key={`title-${title.id}`}
                                        onClick={() =>
                                            isPressLike ? onOpenPublication(title.id) : onOpenTitle(title.id)
                                        }
                                        className="command-palette__item"
                                    >
                                        <ListItemAvatar>
                                            <TitleCover
                                                title={title.name}
                                                coverUrl={title.avatarUrl ?? undefined}
                                                style={{ width: 40 }}
                                            />
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={title.name}
                                            secondary={[title.ean13, `#${title.id}`].filter(Boolean).join(" • ")}
                                            className="command-palette__item-text"
                                        />
                                        <button
                                            className="command-palette__item-filter"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onFilterTitleInManagement(title.ean13, title.name);
                                            }}
                                        >
                                            {filterLabel}
                                        </button>
                                    </ListItemButton>
                                ))}
                            </ul>
                        </li>
                    )}

                    {searchResults.articles.length > 0 && (
                        <li>
                            <ul>
                                {searchResults.articles.map((article) => (
                                    <ListItemButton
                                        key={`article-${article.id}`}
                                        onClick={() => onOpenArticle(article.titleId, article.id)}
                                        className="command-palette__item"
                                    >
                                        <ListItemAvatar>
                                            <Avatar
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    fontSize: 14,
                                                }}
                                            >
                                                {getInitials(article.name ?? article.titleName ?? "")}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={article.name || article.titleName}
                                            secondary={[
                                                article.titleName,
                                                [article.beneficiarySurname, article.beneficiaryName]
                                                    .filter(Boolean)
                                                    .join(" "),
                                                article.reference,
                                                `#${article.id}`,
                                            ]
                                                .filter(Boolean)
                                                .join(" • ")}
                                            className="command-palette__item-text"
                                        />
                                        <button
                                            className="command-palette__item-filter"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onFilterArticleInManagement(article.name ?? article.titleName);
                                            }}
                                        >
                                            {filterLabel}
                                        </button>
                                    </ListItemButton>
                                ))}
                            </ul>
                        </li>
                    )}

                    {searchResults.beneficiaries.length > 0 && (
                        <li>
                            <ul>
                                {searchResults.beneficiaries.map((b) => (
                                    <ListItemButton
                                        key={`beneficiary-${b.id}`}
                                        onClick={() => onOpenBeneficiary(b.id)}
                                        className="command-palette__item"
                                    >
                                        <ListItemAvatar>
                                            <Avatar
                                                src={b.avatarUrl ?? ""}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    fontSize: 14,
                                                    backgroundColor: variables["beneficiary-avatar-background"],
                                                    color: variables["beneficiary-avatar-letter"],
                                                }}
                                            >
                                                {getInitials(`${b.name ?? ""} ${b.surname ?? ""}`.trim())}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={[b.surname, b.name].filter(Boolean).join(" ")}
                                            secondary={[b.pseudonym, `#${b.id}`].filter(Boolean).join(" • ")}
                                            className="command-palette__item-text"
                                        />
                                        <button
                                            className="command-palette__item-filter"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onFilterBeneficiaryInManagement(b.surname, b.name);
                                            }}
                                        >
                                            {filterLabel}
                                        </button>
                                    </ListItemButton>
                                ))}
                            </ul>
                        </li>
                    )}
                </>
            )}

            {showSpinner && (
                <div className="command-palette__empty">
                    <Spinner sx={{ transform: "scale(0.6)" }} />
                </div>
            )}

            {showEmptyState && <div className="command-palette__empty">{t("CommandPalette.NoResults")}</div>}
        </>
    );
};
